import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useLocation } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TableCell,
  TableRow,
  TextField,
} from "@material-ui/core";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  DateField,
  FilterSelect,
  PAGE_SIZE,
  PageHeader,
  ServerPagination,
  errorMessage,
  fmtDate,
  fmtDateTime,
  fmtPeriod,
  gbp,
  monthOptions,
  useCloStyles,
} from "./shared";

type Filters = { brandId: string; period: string };
type Form = { brandId: string; period: string; amount: string; receivedOn: string; reference: string };

const EMPTY_FILTERS: Filters = { brandId: "", period: "" };

const COLUMNS = [
  { label: "Brand" },
  { label: "Period" },
  { label: "Amount", numeric: true },
  { label: "Received" },
  { label: "Reference" },
  { label: "Allocated", numeric: true },
  { label: "Unallocated", numeric: true },
  { label: "Lines paid", numeric: true },
  { label: "Recorded by" },
  { label: "Recorded at" },
];

const emptyForm = (brandId = "", period = ""): Form => ({
  brandId,
  period,
  amount: "",
  receivedOn: moment().format("YYYY-MM-DD"),
  reference: "",
});

const PaymentsList = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(emptyForm());
  const [saving, setSaving] = useState(false);
  const names = useBrandNames(payments.map((p) => p.brand_id));

  // Overrides exist because setState has not applied yet when Clear re-fetches.
  const fetchPayments = async (nextPage = 1, overrides?: Filters) => {
    const active = overrides || filters;
    const params: any = { page: nextPage, pageSize: PAGE_SIZE };
    if (active.brandId) params.brand_id = active.brandId;
    if (active.period) params.period = active.period;
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_PAYMENTS, getConfigGet(params));
      setPayments(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setPage(nextPage);
    } catch (err) {
      notify(errorMessage(err, "Failed to load payments"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const brandId = query.get("brandId") || "";
    const period = query.get("period") || "";
    if (brandId || period) {
      const prefilled = { brandId, period };
      setFilters(prefilled);
      setForm(emptyForm(brandId, period));
      setOpen(true);
      fetchPayments(1, prefilled);
    } else {
      fetchPayments();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => {
    setFilters(EMPTY_FILTERS);
    fetchPayments(1, EMPTY_FILTERS);
  };

  const setField = (key: keyof Form) => (e: React.ChangeEvent<any>) =>
    setForm({ ...form, [key]: e.target.value });

  const openCreate = () => {
    setForm(emptyForm(filters.brandId, filters.period));
    setOpen(true);
  };

  const validate = (): string | null => {
    if (!/^\d+$/.test(form.brandId)) return "Pick a brand";
    if (!/^\d{4}-\d{2}$/.test(form.period)) return "Pick a period";
    if (!/^\d+(\.\d{1,2})?$/.test(form.amount.trim()) || parseFloat(form.amount) <= 0) {
      return "Amount must be a positive £ value with at most 2 decimals";
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.receivedOn)) return "Pick the date the money was received";
    if (!form.reference.trim()) return "Reference is required";
    return null;
  };

  const save = async () => {
    const invalid = validate();
    if (invalid) {
      notify(invalid, "warning");
      return;
    }
    const amountMinor = Math.round(parseFloat(form.amount) * 100);
    if (
      !window.confirm(
        `Record ${gbp(amountMinor)} received on ${fmtDate(form.receivedOn)} from ${brandLabel(names, form.brandId)} for ${fmtPeriod(form.period)}?`
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      const res: any = await axios.post(
        ENDPOINTS.CLO_PAYMENTS,
        {
          brandId: Number(form.brandId),
          period: form.period,
          amountMinor,
          receivedOn: form.receivedOn,
          reference: form.reference.trim(),
        },
        { headers: getConfigPost() }
      );
      const r = res.data || {};
      notify(
        `Payment recorded: ${gbp(r.allocatedMinor)} allocated across ${r.linesPaid ?? 0} lines, ${gbp(r.unallocatedMinor)} unallocated`,
        "info"
      );
      setOpen(false);
      fetchPayments(1);
    } catch (err) {
      notify(errorMessage(err, "Failed to record payment"), "warning");
    } finally {
      setSaving(false);
    }
  };

  const filtered = !!(filters.brandId || filters.period);

  return (
    <div className={classes.main}>
      <PageHeader
        title="Payments"
        subtitle="Money received from retailers against approved invoices"
        actions={
          <Button variant="contained" color="primary" disableElevation onClick={openCreate}>
            Record payment
          </Button>
        }
      />

      <div className={classes.filterContainer}>
        <BrandPicker
          className={classes.filterField}
          value={filters.brandId}
          onChange={(brandId) => setFilters({ ...filters, brandId })}
        />
        <FilterSelect
          label="Period"
          value={filters.period}
          onChange={(period) => setFilters({ ...filters, period })}
          options={monthOptions()}
        />
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={() => fetchPayments(1)} disabled={loading}>
          Search
        </Button>
        <Button className={classes.filterButton} variant="outlined" onClick={clear}>Clear</Button>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable
          columns={COLUMNS}
          isEmpty={payments.length === 0}
          empty={
            filtered
              ? "No payments match these filters."
              : "No payments recorded. Use Record payment once a retailer pays an approved invoice."
          }
          footer={<ServerPagination page={page} total={total} onChange={fetchPayments} />}
        >
          {payments.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>{brandLabel(names, p.brand_id)}</TableCell>
              <TableCell>{fmtPeriod(p.period)}</TableCell>
              <TableCell align="right"><strong>{gbp(p.amount_minor)}</strong></TableCell>
              <TableCell>{fmtDate(p.received_on)}</TableCell>
              <TableCell>{p.reference}</TableCell>
              <TableCell align="right">{gbp(p.allocated_minor)}</TableCell>
              <TableCell align="right">{gbp(p.unallocated_minor)}</TableCell>
              <TableCell align="right">{p.lines_paid}</TableCell>
              <TableCell>{p.recorded_by || "—"}</TableCell>
              <TableCell>{fmtDateTime(p.createdAt)}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record retailer payment</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            <BrandPicker value={form.brandId} onChange={(brandId) => setForm({ ...form, brandId })} required />
            <FilterSelect
              label="Period"
              value={form.period}
              onChange={(period) => setForm({ ...form, period })}
              options={monthOptions()}
              allLabel={null}
              fullWidth
            />
            <TextField
              variant="outlined"
              size="small"
              label="Amount"
              value={form.amount}
              onChange={setField("amount")}
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              required
            />
            <DateField
              label="Received on"
              value={form.receivedOn}
              onChange={(receivedOn) => setForm({ ...form, receivedOn })}
              required
              fullWidth
            />
            <TextField variant="outlined" size="small" label="Reference" value={form.reference} onChange={setField("reference")} required />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} color="primary" variant="contained" disableElevation disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Record"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default PaymentsList;
